import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import Paper from "@mui/material/Paper";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET} from "APIclient";
import {useEffect, useState} from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import properties from "constants/ApplicationConstants";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 20, headerAlign: "center", align: "center"},
  {
    field: "ApplyDate",
    headerName: "Coff Against",
    width: 120,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "ApproveDate",
    headerName: "Approval Date",
    width: 120,
    headerAlign: "center",
    align: "center"
  },
  {field: "Reason", headerName: "Reason", width: 200},
  {
    field: "ExpireDate",
    headerName: "Expire Date",
    width: 120,
    headerAlign: "center",
    align: "center"
  },
  {field: "COFFUsed", headerName: "COFF Used", width: 100, headerAlign: "center", align: "center"},
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;
      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        default:
          buttonColor = "secondary";
          statusText = "Unknown";
      }
      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "60px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "Action",
    headerName: "Action",
    type: "icon",
    width: 70,
    headerAlign: "center",
    align: "center",
    renderCell: params => (
      <Tooltip title="View Details" arrow>
        <GridActionsCellItem
          key={`view-${params.id}`}
          icon={<VisibilityIcon />}
          label="View"
          color="primary"
          onClick={() => params.row.handleViewDetails(params.row)}
        />
      </Tooltip>
    )
  }
];

export default function ListCompensatoryOff() {
  const empId = window.sessionStorage.getItem("empId");
  const theme = useTheme();
  const [compOffRequests, setCompOffRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [filteredCompOffRequests, setFilteredCompOffRequests] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [isFetching, setIsFetching] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCompOffRequest, setSelectedCompOffRequest] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const handleClose = () => {
    setSnackOpen(false);
  };

  const fetchCompOffRequests = async (currentPage, currentPageSize, status = "") => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      let url = `employee/coff-list/${empId}?page=${currentPage}&pageSize=${currentPageSize}`;
      if (status) {
        switch (status.toLowerCase()) {
          case "pending":
            url += `&status=1`;
            break;
          case "approved":
            url += `&status=0&approved=1`;
            break;
          case "rejected":
            url += `&status=2`;
            break;
          default:
            break;
        }
      }

      console.log("Fetching URL:", url);
      const response = await GET(url);
      console.log("API Response:", response.data);

      if (response.status === 200) {
        const data = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const totalPagesCount = Math.ceil(totalRecords / currentPageSize) || 1;

        let index = currentPage * currentPageSize + 1;
        const formattedData = data.map(item => {
          let statusLabel = "";
          if (item.status === 1) {
            statusLabel = "Pending";
          } else if (item.approved === 1) {
            statusLabel = "Approved";
          } else if (item.status === 2) {
            statusLabel = "Rejected";
          }

          let coffUsed = "Not Used";
          if (item.isUsed === 1) {
            coffUsed = "Used";
          } else if (item.isUsed === 2) {
            coffUsed = "Expired";
          }

          return {
            id: index++,
            ApplyDate: item.applyDate
              ? dayjs(item.applyDate).format("DD MMM YYYY")
              : "No Apply Date",
            ApproveDate: item.approvDate
              ? dayjs(item.approvDate).format("DD MMM YYYY")
              : "Not Approved",
            Reason: item.reason || "No reason provided",
            ExpireDate: item.expireDate
              ? dayjs(item.expireDate).format("DD MMM YYYY")
              : "No Expiry Date",
            COFFUsed: coffUsed,
            status: statusLabel,
            reason: item.reason,
            handleViewDetails: handleViewDetails
          };
        });

        setCompOffRequests(formattedData);
        setFilteredCompOffRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(totalPagesCount);
      }
    } catch (error) {
      console.error("Error fetching compensatory off requests:", error);
      if (
        error.response &&
        (error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403)
      ) {
        setSnackMessage(error.response.data.message || "Error fetching compensatory off requests.");
        setSnackColor("error");
        setSnackOpen(true);
      } else {
        setSnackMessage("Error fetching compensatory off requests.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchCompOffRequests(page, pageSize, appliedStatusFilter);
  }, []);

  const handleFilterChange = e => {
    setStatusFilter(e.target.value);
  };

  const handleSearchClick = () => {
    setAppliedStatusFilter(statusFilter);
    setPage(0);
    setPaginationModel({page: 0, pageSize: pageSize});
    fetchCompOffRequests(0, pageSize, statusFilter);
  };

  const handleViewDetails = compOffRequest => {
    setSelectedCompOffRequest(compOffRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCompOffRequest(null);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setPaginationModel(prev => ({...prev, page: newPage}));
    fetchCompOffRequests(newPage, pageSize, appliedStatusFilter);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      setPaginationModel(prev => ({...prev, page: lastPageIndex}));
      await fetchCompOffRequests(lastPageIndex, pageSize, appliedStatusFilter);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Compensatory Off
                </Typography>
                <FormControl sx={{gridColumn: "span 3"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={statusFilter}
                    label="Status"
                    name="status"
                    onChange={handleFilterChange}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="contained"
                  color="info"
                  onClick={handleSearchClick}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
            </Card>
          </AppBar>
      </MDBox>
            
      <MDBox>
        <Paper>
          <DataGrid
            rows={filteredCompOffRequests}
            columns={columns}
            paginationMode="server"
            rowCount={totalItems}
            pageSizeOptions={[20]}
            paginationModel={paginationModel}
            loading={isFetching}
            hideFooter
            size="small"
            aria-label="a dense table"
            sx={{
              fontFamily: properties.fontFamily,
              fontWeight: 400,
              fontSize: "14px",
              height: "450px",
              "& .MuiDataGrid-scrollbar": {
                width: "10px !important",
              },
            }}
          />
          <CustomPagination
            page={page}
            pageSize={pageSize}
            totalRows={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onGoToLastPage={handleGoToLastPage}
            loading={isFetching}
          />
        </Paper>
      </MDBox>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "700px",
            maxWidth: "none"
          }
        }}
      >
        <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <MDTypography variant="h6" fontWeight="bold" sx={{fontFamily: properties.fontFamily}}>
            Compensatory Off Request Details
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{padding: 2}}>
          {selectedCompOffRequest && (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Applied Date"
                    value={selectedCompOffRequest.ApplyDate}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Approval Date"
                    value={selectedCompOffRequest.ApproveDate}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Expire Date"
                    value={selectedCompOffRequest.ExpireDate}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Status"
                    value={selectedCompOffRequest.status}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="COFF Used"
                    value={selectedCompOffRequest.COFFUsed}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Reason"
                    value={selectedCompOffRequest.Reason}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                  />
                </Grid>
                <Divider sx={{my: 2}} />
              </Grid>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={snackColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}